// --- Page registry (ocn-S184-P0) --------------------------------
// DESIGN: Page routing preserves the shared dark console shell. Subscriber
// Metrics is an aggregate outcome/usage view, while MCP Connect guides scoped
// onboarding without becoming an operator substrate surface.
// The single encapsulation of the dashboard's `page` string switch that
// previously lived inline in App.tsx (arch-review Finding 5 #5). Each entry
// maps a page id (or dynamic prefix) to its render, given a RouterCtx bag of
// the shared shell state. The tier gate is applied by AppRouter BEFORE this
// resolves, using app/tier-visibility.ts -- this module is pure routing.

import React from "react";
import api, { type ProjectSummary } from "../api";

import { OverviewPage } from "../pages/Overview";
import SubscriberChat from "../pages/SubscriberChat";
import { RunsPage } from "../pages/Runs";
import { Logs } from "../pages/Logs";
import { RunDetail } from "../pages/RunDetail";
import { InfraPage } from "../pages/Infra";
import { CostsPage } from "../pages/Costs";
import { MetricsPage } from "../pages/Metrics";
import { McpConnectPage } from "../pages/McpConnectPage";
import { SyncPage } from "../pages/Sync";
import { SafetyPage } from "../pages/Safety";
import { CredentialsPage } from "../pages/Credentials";
import { CredentialWizard } from "../pages/CredentialWizard";
import SettingsPage from "../pages/Settings";
import { JournalPage } from "../pages/Journal";
import GuardianPage from "../pages/Guardian";
import { SkillsPage } from "../pages/SkillsPage";
import { ModuleBrowser } from "../pages/ModuleBrowser";
import { LaunchWizard } from "../pages/LaunchWizard";
import WizardPage from "../pages/WizardPage";
import { OnboardingWizard } from "../pages/OnboardingWizard";
import { ProjectChatPage } from "../pages/project-chat/ProjectChatPage";
import { ProjectDetailPage } from "../pages/project-detail/ProjectDetailPage";
import { AddProjectPage } from "../pages/add-project/AddProjectPage";
import { RepoListPage } from "../pages/RepoListPage";
import ConfigPage from '../pages/Config';
import { IntelPage } from '../pages/IntelPage';
import { ProfilePage, SubscriptionPage } from '../pages/Account';
import { parseSyncRoute } from '../lib/sync-route';

/** Shared shell state the page renders need. Owned by App.tsx, passed down. */
export interface RouterCtx {
  go: (p: string) => void;
  projects: ProjectSummary[];
  projLoading: boolean;
  refetchProjects: () => void;
  chatPrefillRef: React.MutableRefObject<string | null>;
  chatSessionRef: React.MutableRefObject<string | null>;
  lastActiveProjectRef: React.MutableRefObject<string | null>;
}

type PageRender = (ctx: RouterCtx) => React.ReactNode;

// Exact-id (static) routes. Verbatim from the prior App.tsx page switch.
const STATIC_PAGES: Record<string, PageRender> = {
  intel: () => <IntelPage />,
  overview: (ctx) => (
    <>
      {ctx.projLoading && (
        <div className="empty-state"><div className="spinner" /><span className="muted">Loading projects...</span></div>
      )}
      <OverviewPage
        go={ctx.go}
        projects={ctx.projects}
        onFixIt={(prompt: string, projectId?: string) => {
          ctx.chatPrefillRef.current = prompt;
          // A risk that lives in one repo opens THAT project's chat (checklist +
          // prefilled request), not the generic workspace chat.
          if (projectId) {
            void api.createSubscriberChat(projectId)
              .then((r: { chatSessionId: string }) => ctx.go(`subscriber-chat:chat:${r.chatSessionId}`))
              .catch(() => ctx.go(`subscriber-chat:project:${projectId}`));
            return;
          }
          ctx.go('subscriber-chat');
        }}
      />
    </>
  ),
  'subscriber-chat': (ctx) => (
    <div style={{ margin: '0 calc(-1 * var(--space-6))', padding: 0, height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <SubscriberChat key={`subscriber-chat:${ctx.chatSessionRef.current ?? 'new'}`} chatSessionId={ctx.chatSessionRef.current} onNavigate={ctx.go} />
    </div>
  ),
  runs: (ctx) => <RunsPage projects={ctx.projects} />,
  logs: (ctx) => <Logs onSelectRun={(id: string) => ctx.go(`run:${id}`)} />,
  infra: () => <InfraPage />,
  costs: (ctx) => <CostsPage projects={ctx.projects} />,
  metrics: (ctx) => <MetricsPage projects={ctx.projects} onNavigate={ctx.go} />,
  'mcp-connect': (ctx) => <McpConnectPage projects={ctx.projects} onNavigate={ctx.go} />,
  sync: (ctx) => <SyncPage onBack={() => ctx.go("overview")} projectId={ctx.projects[0]?.project_id} onNavigate={ctx.go} />,
  safety: () => <SafetyPage />,
  credentials: () => <CredentialsPage />,
  config: (ctx) => <ConfigPage projectId={ctx.projects[0]?.project_id} projects={ctx.projects} />,
  'account-setup': (ctx) => <CredentialWizard mode="account" onComplete={() => ctx.go('overview')} onBack={() => ctx.go('overview')} />,
  settings: () => <SettingsPage />,
  // My profile (name, email, password) and Subscription (plan, credits, billing).
  // `account` is kept as an alias: Stripe return URLs point at ?page=account.
  profile: (ctx) => <ProfilePage go={ctx.go} />,
  subscription: (ctx) => <SubscriptionPage go={ctx.go} />,
  account: (ctx) => <SubscriptionPage go={ctx.go} />,
  journal: (ctx) => <JournalPage projects={ctx.projects} />,
  guardian: (ctx) => ctx.projects[0] ? <GuardianPage projectId={ctx.projects[0].project_id} /> : null,
  skills: () => <SkillsPage />,
  modules: (ctx) => ctx.projects[0] ? <ModuleBrowser projectId={ctx.projects[0].project_id} /> : null,
  addproject: (ctx) => <AddProjectPage go={ctx.go} registeredProjects={ctx.projects} onProjectAdded={ctx.refetchProjects} />,
  'launch-wizard': (ctx) => <LaunchWizard onComplete={() => { ctx.refetchProjects(); ctx.go('overview'); }} onBack={() => ctx.go('overview')} />,
  wizard: (ctx) => <WizardPage onComplete={() => { ctx.refetchProjects(); ctx.go('overview'); }} />,
  'new-project': (ctx) => <OnboardingWizard go={(p: string) => { if (p.startsWith('project:')) ctx.refetchProjects(); ctx.go(p); }} />,
  // ocn-S278-P0: repo picker for the "Review & fix a repo" entry-point flow.
  'repo-list': (ctx) => <RepoListPage go={ctx.go} projects={ctx.projects} />,
  // ocn-S278-P0: subscriber-chat with initial mode from entry-point card.
  // subscriber-chat:app_wizard        → chat in build mode (no wizard gating screen)
  // subscriber-chat:sync_find_fix     → chat in sync/fix mode, no project pre-selected
  // subscriber-chat:sync_find_fix:<id> → chat in sync/fix mode, project pre-selected
  'subscriber-chat:app_wizard': (ctx) => (
    <div style={{ margin: '0 calc(-1 * var(--space-6))', padding: 0, height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <SubscriberChat key={`subscriber-chat:app-wizard:${ctx.chatSessionRef.current ?? 'new'}`} chatSessionId={ctx.chatSessionRef.current} initialMode="app_wizard" onNavigate={ctx.go} />
    </div>
  ),
  // UI-only MCP Connect handoff. This adds visual context to the existing
  // subscriber chat without introducing a new chat mode or backend contract.
  'subscriber-chat:mcp': (ctx) => (
    <div style={{ margin: '0 calc(-1 * var(--space-6))', padding: 0, height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <SubscriberChat key={`subscriber-chat:mcp:${ctx.chatSessionRef.current ?? 'new'}`} chatSessionId={ctx.chatSessionRef.current} mcpContext onNavigate={ctx.go} />
    </div>
  ),
};

// Dynamic (prefix) routes. Verbatim from the prior App.tsx page switch.
interface PrefixRoute {
  prefix: string;
  render: (ctx: RouterCtx, page: string) => React.ReactNode;
}

/** An explicit repo selection resolves its owned project chat, never a retained chat. */
function SelectedRepoReviewChat({ projectId, chatSessionRef, onNavigate }: {
  projectId: string;
  chatSessionRef: RouterCtx['chatSessionRef'];
  onNavigate: RouterCtx['go'];
}): React.ReactElement {
  React.useEffect(() => { chatSessionRef.current = null; }, [chatSessionRef, projectId]);
  return <SubscriberChat projectId={projectId} initialMode="sync_find_fix" initialProjectId={projectId} onNavigate={onNavigate} />;
}

const PREFIX_PAGES: PrefixRoute[] = [
  {
    prefix: 'subscriber-chat:sync:',
    render: (ctx, page) => {
      const [, , chatSessionId, syncId] = page.split(':');
      return chatSessionId && syncId ? <SubscriberChat key={`live-codex:${chatSessionId}:${syncId}`}
        chatSessionId={chatSessionId} syncId={syncId} onNavigate={ctx.go} /> : null;
    },
  },
  {
    // Entry-created Forge chat: preserve the new thread while selecting sync mode.
    prefix: 'subscriber-chat:entry:',
    render: (ctx, page) => {
      const [, , , chatSessionId, mode, projectId] = page.split(':');
      return chatSessionId ? <SubscriberChat key={`subscriber-chat:entry:${chatSessionId}`} chatSessionId={chatSessionId} initialMode={mode === 'sync' ? 'sync_find_fix' : undefined} initialProjectId={projectId || null} onNavigate={ctx.go} /> : null;
    },
  },
  {
    prefix: 'subscriber-chat:chat:',
    render: (ctx, page) => {
      const chatSessionId = page.split(':')[2] || null;
      return <SubscriberChat key={`subscriber-chat:${chatSessionId ?? 'new'}`} chatSessionId={chatSessionId} onNavigate={ctx.go} />;
    },
  },
  {
    // One durable subscriber-chat thread per project.
    prefix: 'subscriber-chat:project:',
    render: (ctx, page) => {
      const projectId = page.split(':')[2];
      return projectId ? <div style={{ margin: '0 calc(-1 * var(--space-6))', padding: 0, height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
        <SubscriberChat key={`subscriber-chat:project:${projectId}`} projectId={projectId} onNavigate={ctx.go} />
      </div> : null;
    },
  },
  {
    // ocn-S278-P0: subscriber-chat:sync_find_fix[:projectId]
    // subscriber-chat:sync_find_fix          → chat in sync mode, no project
    // subscriber-chat:sync_find_fix:<projId> → chat in sync mode, project pre-selected
    prefix: 'subscriber-chat:sync_find_fix',
    render: (ctx, page) => {
      const parts = page.split(':');
      const projectId = parts[2] || null;
      return (
        <div style={{ margin: '0 calc(-1 * var(--space-6))', padding: 0, height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
          {projectId
            ? <SelectedRepoReviewChat key={`subscriber-chat:sync:${projectId}`} projectId={projectId} chatSessionRef={ctx.chatSessionRef} onNavigate={ctx.go} />
            : <SubscriberChat key={`subscriber-chat:sync:${ctx.chatSessionRef.current ?? 'new'}`} chatSessionId={ctx.chatSessionRef.current} initialMode="sync_find_fix" onNavigate={ctx.go} />}
        </div>
      );
    },
  },
  {
    prefix: 'run:',
    render: (ctx, page) => <RunDetail runId={page.split(":")[1]} onBack={() => ctx.go("logs")} />,
  },
  {
    // sync:<projectId> — Sync page scoped to a specific project (the static
    // `sync` route always shows projects[0], which is wrong when approving a
    // specific project's sync from the Overview action panel).
    prefix: 'sync:',
    render: (ctx, page) => {
      const route = parseSyncRoute(page);
      return route ? <SyncPage key={`sync:${route.projectId}:${route.runId ?? ''}`} onBack={() => ctx.go('overview')} projectId={route.projectId} expectedRunId={route.runId} onNavigate={ctx.go} /> : null;
    },
  },
  {
    prefix: 'project-setup:',
    render: (ctx, page) => {
      const id = page.split(':')[1];
      return <CredentialWizard mode="project" projectId={id} onComplete={() => ctx.go(`project:${id}`)} onBack={() => ctx.go(`project:${id}`)} />;
    },
  },
  {
    prefix: 'hierarchy-chat:',
    render: (_ctx, page) => {
      const parts = page.split(":");
      return parts[1] && parts[2] ? <ProjectChatPage projectId={parts[1]} chatId={parts[2]} /> : null;
    },
  },
  {
    prefix: 'project:',
    render: (ctx, page) => {
      const id = page.split(":")[1];
      return id ? (
        <ProjectDetailPage
          projectId={id}
          go={ctx.go}
          onProjectRenamed={ctx.refetchProjects}
          onFixIt={(prompt: string) => { ctx.chatPrefillRef.current = prompt; ctx.go('subscriber-chat'); }}
        />
      ) : null;
    },
  },
];

/**
 * Resolve and render the page for `page`. Returns null for an unknown page id
 * (matches the prior behavior: an unmatched page rendered an empty main area).
 * Callers must apply the tier gate before invoking this.
 */
export function renderPage(page: string, ctx: RouterCtx): React.ReactNode {
  const staticRender = STATIC_PAGES[page];
  if (staticRender) return staticRender(ctx);
  for (const route of PREFIX_PAGES) {
    if (page.startsWith(route.prefix)) return route.render(ctx, page);
  }
  return null;
}
